package com.crowpay.views.components.project.work.scope

import com.crowpay.*
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.completionResponse.CompletionFlowPermissions
import com.crowpay.completionResponse.CompletionResponse
import com.crowpay.completionResponse.*
import com.crowpay.extensions.actionName
import com.crowpay.extensions.confirmationText
import com.crowpay.extensions.isUnread
import com.crowpay.extensions.markAsReadUpdateOnRemove
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.*
import com.crowpay.utils.validation.Validator
import com.crowpay.views.components.*
import com.crowpay.views.components.files.*
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.dialogs.DisputeWorkItemConfirmation
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.dialogs.GenericDialog

import com.crowpay.views.dialogs.MinorAdjustmentsDialog
import com.crowpay.views.dialogs.WorkItemRequestSignOffDialog
import com.crowpay.views.screens.client.ClientProject
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.screens.common.DialogWithDialogOrPage
import com.crowpay.views.screens.common.secondDialog
import com.crowpay.views.screens.contractor.ContractorProject
import com.crowpay.views.theming.*
import com.lightningkite.UUID
import com.lightningkite.kiteui.ExternalServices
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import com.lightningkite.now
import com.lightningkite.serialization.notNull
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant
import kotlin.collections.map

class LineItemCompletionForm private constructor(
    val lineItem: AdjustedLineItem,
    val project: Project,
    val messages: List<LineItemCompletionMessage>,
    val replies: Readable<List<CompletionMessageComment>>,
    val client: Client,
    val contractor: Contractor,
    val lens: ProjectLens,
    val scopeInfo: ScopeSetInfo? = null,
) : DialogWithDialogOrPage, Validator() {
    override val title: Readable<String> = Constant("${lineItem.name} Form")

    data class ScopeSetInfo(val id: UUID, val name: String, val price: Long)

    private val ViewWriter.outerPadding get() = if (viewIsNarrow) null else AppDimensions.fullIndent
    private fun RowOrCol.outerPadding() { outerPadding?.let { space(it) } }

    val previousMessage = messages.lastOrNull()

    val isClient = lens.isClient()

    val permissions = CompletionFlowPermissions(lineItem.wraps, isClient, messages, preSubstantialCompletion = project.preSubstantialCompletion)

    val proposeResolution = Property(permissions.inResolveLater && isClient)

    private fun ViewWriter.closeForm() {
        dialogScreenNavigator.dismiss()
        if (screenNavigator === mainScreenNavigator) mainScreenNavigator.dismiss()
    }

    // Parts of response construction
    val raiseIssue = Property(false)
    val fileDispute = Property<DisputeType?>(null)

    // TODO: This system was created before CompletionResponseSerializable existed. In the future this should probably
    //      be refactored to simply use that data class instead of these individual properties.
    val response = Property<CompletionResponse?>(
        if (permissions.inResolveLater && isClient) CompletionResponse.ClientResolution else null
    ).validateNotNull()

    val maxCredit = lineItem.wraps.remainingFundsWithRetention(project.retention)
    val credit = Property<Double?>(null)

    val creditCondition = addCondition {
        when (response()) {
            is CompletionResponse.Credit -> credit().let {
                it != null && it > 0 && it <= maxCredit
            }
            is CompletionResponse.Handled,
            is CompletionResponse.ForceComplete,
            is CompletionResponse.Remedy -> credit().let {
                it == null || (it > 0 && it <= maxCredit)
            }
            else -> true
        }
    }


    val files = SignalingList<FileData>()

    val minChars = 35
    val message = Property("").validate { it.length >= minChars }


    val activeTimers = shared {
        notNullSession().projectTimers.query(
            Query { (it.lineItem eq lineItem._id) and (it.executed eq null) }
        )()
    }

    val autoCompletesAt = shared { activeTimers().firstOrNull { it.action == TimerAction.CompleteLineItem }?.executesAt }

    private enum class ResponseState(val displayName: String, val color: Color?) {
        Completed("COMPLETED", null),
        Dispute("DISPUTE", AppColors.red.main),
        FileDispute("FILE A DISPUTE", AppColors.red.main),
        Resolving("RESOLVING", null),
        Issue("ISSUE", AppColors.yellow.main),
        RaiseIssue("RAISE ISSUE", AppColors.yellow.main),
        NotReady("NOT READY", null),
    }

    private val responseState = shared {
        when {
            lineItem.state == LineItemState.Complete -> ResponseState.Completed
            fileDispute() != null -> ResponseState.FileDispute
            raiseIssue() -> ResponseState.RaiseIssue
            lineItem.state == LineItemState.Resolving -> ResponseState.Resolving
            permissions.disputeWasFiled -> ResponseState.Dispute
            permissions.issueWasRaised -> ResponseState.Issue
            else -> ResponseState.NotReady
        }
    }

    private val resolving = shared {
        proposeResolution() ||
                permissions.clientProposingResolution ||
                (!isClient && lineItem.state in setOf(LineItemState.Disputed, LineItemState.ResolveLater))
    }

    private suspend fun sendResponse() {
        val session = notNullSession()
        val sent = session.nonCached.lineItemCompletionMessage.sendMessage(
            LineItemCompletionMessage(
                project = project._id,
                lineItem = lineItem._id,
                sender = session.userId,
                clientMessage = isClient,
                responseTypeSerializable = response()?.let {
                    when (it) {
                        is CompletionResponse.ClientResponse -> it.copy(
                            raiseIssue = raiseIssue.value,
                            fileDispute = fileDispute.value
                        )
                        is CompletionResponse.Credit -> it.copy(credit = credit.value?.toLong() ?: return)
                        is CompletionResponse.Remedy -> it.copy(credit = credit.value?.toLong())
                        is CompletionResponse.Handled -> it.copy(credit = credit.value?.toLong())
                        is CompletionResponse.ForceComplete -> it.copy(credit = credit.value?.toLong())
                        else -> it
                    }.serializable()
                } ?: return,
                message = message(),
                attachments = files.resolveProjectAttachments()
            )
        )
        session.lineItemCompletionMessages.totallyInvalidate()
        session.lineItemCompletionMessages.localInsert(sent)
    }

    val historyExpanded = LazyProperty {
        replies().any { notNullSession().userId !in it.sentAndReadBy }
    }

    override fun DismissBackground.renderDialog(): ViewModifiable {
        return scrolls - col {
            spacing = AppDimensions.fullIndent
            space()
            sizeConstraints(width = AppDimensions.pageWidth) - atTopCenter - dialog - page(true)
            space(2.0)
        }
    }

    override fun ViewWriter.renderAsPage(): ViewModifiable = scrolls - page(false)

    private fun ViewWriter.page(renderingAsDialog: Boolean): ViewModifiable =
        col {
            spacing = 0.px
            row {
                spacing = 0.px
                expanding - compact - FormHeader.onNext - xlTitle {
                    align = Align.Center
                    dynamicTheme {
                        if (responseState() == ResponseState.Resolving) PurpleSection else null
                    }
                    ::content { responseState().displayName }
                }
                if (renderingAsDialog) sizeConstraints(width = 3.25.rem) - secondaryButton - button {
                    centered - icon(Icon.close, "Close Form")
                    onClick {
                        closeForm()
                    }
                }
            }

            row {
                spacing = 0.px
                outerPadding()
                sizeConstraints(height = 5.rem) - expanding - SubtitleSemantic.onNext - row {
                    centered - compact - expanding - rowCollapsingToColumn(AppDimensions.normalCollapseWidth) {
                        row {
                            centered - icon {
                                ::exists { responseState().color != null }
                                source = Icon.crowPayWarning.resize(2.rem)
                                dynamicTheme { responseState().color?.let(::ForegroundSemantic) }
                            }
                            centered - LineItemStateSemantic(lineItem.state).onNext - text(lineItem.state.displayName)
                        }
                        if (viewIsWide) space(0.2)
                        gravity(Align.Start, Align.Center) - text {
                            wraps = false
                            ellipsis = true
                            ::content {
                                when {
                                    lineItem.state.isComplete() -> ""
                                    permissions.mustRequestComplete -> "Waiting on Sign-Off Request"
                                    permissions.mustRequestReviewOfRemedy -> "Waiting on Sign-Off Request"
                                    permissions.awaitingResponse -> response()?.let { "[${it.shortDisplayName}]" } ?: ""
                                    else -> "Waiting on response from ${if (isClient) "Contractor" else "Homeowner"}"
                                }
                            }
                        }
                    }
                    centered - sizeConstraints(width = 7.rem) - compact - col {
                        spacing = 2.dp
                        text {
                            align = Align.Center
                            content = now().format(Formats.mmddyyyy)
                        }
                        text {
                            align = Align.Center
                            content = "12:34:56"
                        }
                    }
                }
                outerPadding()
            }

            separator()

            row {
                spacing = 0.px
                outerPadding()
                expanding - col {
                    space(0.15)
                    spacing = AppDimensions.majorColumnSpacing

                    col {
                        col {
                            spacing = 0.5.rem
                            bodyBold(if (scopeInfo != null) "Scope Set" else "Work Item")
                            row {
                                expanding - row {
                                    expanding - centered - subTitle(scopeInfo?.name ?: lineItem.name)
                                    centered - subTitle(scopeInfo?.price?.renderDollars() ?: lineItem.price.renderDollars())
                                }
                                centered - secondaryButton - button {
                                    specCenteredText("View Item")
                                    onClick("View Item") {
                                        mainScreenNavigator.run {
                                            if (stack.value.lastOrNull() !is ProjectView) {
                                                navigate(
                                                    if (isClient) ClientProject(project._id)
                                                    else ContractorProject(project._id)
                                                )
                                            }
                                        }
                                        JumpTo.LineItem(lineItem._id, animate = false)
                                        dialogScreenNavigator.dismiss()
                                    }
                                }
                            }
                            greySeparator()
                        }

                        renderBanners()
                    }

                    col {
                        outlined - stack {
                            col {
                                spacing = 0.px
                                leftExpandButton(historyExpanded, highlightOnExpand = false) { boldSubTitle("Form History") }

                                onlyWhen { historyExpanded() } - col {
                                    space(0.0)  // don't remove until spacing is fixed in v5
                                    previousMessages()
                                }
                            }
                        }

                        outlined - col {
                            boldSubTitle("Most Recent Response")
                            renderMessage(messages.last())
                        }
                    }

                    if (lineItem.state != LineItemState.Complete) {
                        responseForm()

                        space(0.5)

                        actionBar()
                    }

                    space(0.15)
                }
                outerPadding()
            }
        }

    private val banners = shared {
        banners {
            val unit = if (this@LineItemCompletionForm.scopeInfo != null) "scope" else "item"

            when {
                fileDispute() != null -> info("File dispute info text")
                raiseIssue() -> info("Raise issue info text")
                isClient && permissions.awaitingResponse -> "Info explaining minor adjustment and not ready"
                isClient && !permissions.awaitingResponse ->
                    """
                Your feedback has been sent to the contractor.
    
                The contractor will review your concerns and communicate with you about their resolution plan. They may adjust, complete outstanding items, or clarify why they feel it should be signed off.
                """.trimIndent()
            }

            if (!isClient) {
                when (lineItem.state) {
                    LineItemState.NotApproved -> {
                        info(
                            """
                            Use CrowPay’s messaging to communicate with the homeowner, address their feedback, and align expectations. This is your opportunity to clarify misunderstandings, resolve concerns, and gain approval. Clear and prompt communication here can prevent tensions from escalating into formal issues or disputes.
                        """.trimIndent()
                        )
                        warn(
                            """
                            Review carefully and then address the homeowner’s feedback before resubmitting, doing more work if necessary. Focus on providing the quality and clarity needed for approval.

                            Resubmitting prematurely may delay progress and impact trust, which could lead to a formal issue or dispute.
                        """.trimIndent()
                        )
                    }

                    else -> {}
                }
            }

            if (lineItem.complete == null) {
                val autoRelease = autoCompletesAt()
                if (isClient) {
                    if (autoRelease != null) {
                        val countdown = Countdown(autoRelease)
                        warn("homeowner-funds-release-active") { "If no response is given funds will auto-release in ${countdown().formatNatural(includeMinutes = false, includeSeconds = false)}." }
                    } else {
                        if (permissions.disputeWasFiled) info("The timer for this item has been paused while in dispute")
                        else info("The timer for this item has been paused while waiting for the contractor to respond.")
                    }
                } else {
                    if (autoRelease != null) {
                        val countdown = Countdown(autoRelease)
                        info("contractor-funds-release-active") { "If no response is given by the homeowner funds will auto-release in ${countdown().formatNatural(includeMinutes = false, includeSeconds = false)}." }
                    } else {
                        if (permissions.disputeWasFiled) info("The timer for this item has been paused while in dispute")
                        else warn("The timer for this item has been paused while waiting for your response.")
                    }
                }
            }
        }
    }

    private fun ViewWriter.renderBanners() = renderBanners(banners)

    private fun ViewWriter.disputeResolutionSelector() {
        col {
            fieldTheme - select {
                bind(
                    response,
                    Constant(
                        listOf(null) + listOfNotNull(
                            CompletionResponse.Credit(0),
                            CompletionResponse.Remedy(null),
                            CompletionResponse.Appeal.takeIf { !permissions.inResolveLater },
                            CompletionResponse.RequestResolveLater.takeIf { permissions.canProposeResolveLater },
                            CompletionResponse.RequestSiteVisit().takeIf { !permissions.inResolveLater }
                        )
                    )
                ) { it?.shortDisplayName ?: "Select Resolution" }
            }
            onlyWhen { response().let { it is CompletionResponse.Credit || it is CompletionResponse.Remedy } } - col {
                spacing = AppDimensions.cornerRadii
                row {
                    spacing = AppDimensions.cornerRadii
                    centered - bodyBold {
                        ::content { if (response() is CompletionResponse.Remedy) "Credit (Optional)" else "Credit" }
                    }
                    onlyWhen { response() is CompletionResponse.Credit } - colored(AppColors.red.main) - bodyBold("*")
                }
                compact - fieldTheme - validate(creditCondition) - row {
                    centered - body("$")
                    centered - compact - numberInput {
                        keyboardHints = KeyboardHints.integer
                        range = 0.0..Double.MAX_VALUE
                        content bind credit
                        hint = "Max ${maxCredit.renderDollars()}"
                    }
                }
            }
//            onlyWhen { response() is CompletionResponse.RequestSiteVisit } - requiredField("When is the visit?") {
//                localDateTimeField {
//                    val zone = TimeZone.currentSystemDefault()
//                    content bind response.lens(
//                        get = { (it as? CompletionResponse.RequestSiteVisit)?.time?.toLocalDateTime(zone) },
//                        set = {
//                            if (it == null) {
//                                null
//                            }
//                            else CompletionResponse.RequestSiteVisit(it.toInstant(zone))
//                        }
//                    )
//                }
//            }
        }
    }

    private fun ViewWriter.responseSelector() {
        if (isClient) {
            val reasons = response.lens(
                get = { (it as? CompletionResponse.ClientResponse)?.reasons ?: emptySet() },
                set = { CompletionResponse.ClientResponse(reasons = it) }
            )

            scrollsHorizontally - fieldTheme - row {
                for (concernType in LineItemConcern.entries) {
                    buttonTheme - toggleButton {
                        specCenteredText(concernType.displayName)
                        checked bind reasons.contains(concernType)
                    }
                }
            }
        }
        else {
            col {
                fieldTheme - select {
                    bind(
                        edits = response,
                        data = Constant(
                            listOf(null) +
                                    listOfNotNull(
                                        if (permissions.canRequestMoreInfo) CompletionResponse.MoreInfo else null,
                                        CompletionResponse.Handled(null),
                                        CompletionResponse.KeepWorking,
                                        CompletionResponse.ForceComplete(null),
                                        CompletionResponse.RequestSiteVisit(),
                                        if (permissions.canProposeResolveLater) CompletionResponse.RequestResolveLater else null
                                    )
                        ),
                        render = { it?.shortDisplayName ?: "Select a Response" }
                    )
                }
//                onlyWhen { response() is CompletionResponse.RequestSiteVisit } - requiredField("When is the visit?") {
//                    localDateTimeField {
//                        val zone = TimeZone.currentSystemDefault()
//                        content bind response.lens(
//                            get = { (it as? CompletionResponse.RequestSiteVisit)?.time?.toLocalDateTime(zone) },
//                            set = {
//                                if (it == null) null
//                                else CompletionResponse.RequestSiteVisit(it.toInstant(zone))
//                            }
//                        )
//                    }
//                }
                onlyWhen { response().let { it is CompletionResponse.Handled || it is CompletionResponse.ForceComplete } } - field2("Credit (Optional)") {
                    compact - validate(creditCondition) - row {
                        centered - body("$")
                        centered - compact - numberInput {
                            keyboardHints = KeyboardHints.integer
                            range = 0.0..Double.MAX_VALUE
                            content bind credit
                            hint = "Max ${maxCredit.renderDollars()}"
                        }
                    }
                }
            }
        }
    }

    private fun ViewWriter.responseForm() {
        if (permissions.canRespond && !lineItem.state.isComplete() && !permissions.mustRequestComplete && !permissions.mustRequestReviewOfRemedy) col {
            stack {
                reactive {
                    clearChildren()
                    if (resolving()) col {
                        subTitle(
                            if (permissions.clientProposingResolution) "Review Client Resolution"
                            else "Propose Resolution To ${if (isClient) "Contractor" else "Homeowner"}"
                        )

                        if (!isClient) requiredLabel("Resolution Type") {
                            disputeResolutionSelector()
                        }

                        notificationBar(AppColors.yellow) - body {
                            content = "Informative (FYI) Bar"
                        }
                    }
                    else col {
                        subTitle("Respond To ${if (isClient) "Contractor" else "Homeowner"}")

                        if (permissions.canRaiseIssue) compact - row {
                            centered - checkbox {
                                checked bind raiseIssue
                            }
                            centered - body("Raise Issue")
                            space(2.0)
                            secondaryButton - button {
                                specCenteredText("More Info")
                                onClick {
                                    secondDialog.value = GenericDialog(
                                        "More info for raise issue dialog",
                                        dismiss = { dismissSecondDialog() }
                                    )
                                }
                            }
                        }
                        else if (permissions.canFileDispute) compact - row {
                            centered - stack {
                                val size = 1.5.rem
                                centered - sizeConstraints(width = size, height = size) - checkbox {
                                    checked bind fileDispute.lens(
                                        get = { it != null },
                                        set = { null }
                                    )
                                }
                                sizeConstraints(width = size * 1.2, height = size * 1.2) - button {
                                    themeChoice += ThemeDerivation {
                                        it.copy(
                                            id = "nohvr",
                                            derivations = mapOf(
                                                HoverSemantic to { it.withoutBack }
                                            )
                                        ).withoutBack
                                    }
                                    onClick {
                                        if (fileDispute.value != null) {
                                            fileDispute.value = null
                                        } else {
                                            secondDialog.value = DisputeWorkItemConfirmation { selection ->
                                                fileDispute.value = selection
                                                secondDialog.value = null
                                            }
                                        }
                                    }
                                }
                            }

                            centered - body {
                                ::content { "File Dispute" + (fileDispute()?.let { " (${it.displayName})" } ?: "") }
                            }
                        }

                        requiredLabel("Response Type") {
                            responseSelector()
                        }

                        notificationBar(AppColors.yellow) - body {
                            content = "Informative (FYI) Bar"
                        }
                    }
                }
            }

            col {
                spacing = 0.px
                requiredField({
                    if (resolving()) "Explain Resolution"
                    else "Explain your decision or update for this work item."
                }) {
                    sizeConstraints(minHeight = 10.rem) - textArea {
                        hint =
                            if (isClient) "Clearly explain so concerns can be addressed. Min. $minChars characters."
                            else "Clearly explain your position or how concerns can be addressed. Min. $minChars characters."

                        content bind message
                    }
                }
                atEnd - smallBody {
                    ::content {
                        val remaining = minChars - message().length
                        if (remaining <= 0) ""
                        else "($remaining) more characters required"
                    }
                }
            }

            label2("Attachments") {
                existsDefaultFalse { files().isNotEmpty() }
                fileCarousel(files)
            }
        }
    }

    private fun ViewWriter.previousMessages() =
        col {
            launch {
                val session = notNullSession()
                session.lineItemCompletionMessages.markAsReadUpdateOnRemove(this@col, messages)
                session.completionMessageComments.markAsReadUpdateOnRemove(this@col, replies())
            }

            messages.forEach { renderMessage(it) }
        }

    private fun ViewWriter.renderMessage(message: LineItemCompletionMessage) = col {
        spacing = AppDimensions.expandButtonSpace
        val unread = shared {
            message.isUnread(notNullSession().userId)
        }
        FormMessage.onNext - col {
            spacing = 0.dp
            val base = when {
                permissions.resolveLaterAcceptedAt?.let {
                    it < message.created && message.created < (project.preSubstantialCompletion ?: Instant.DISTANT_FUTURE)
                } == true -> LineItemState.ResolveLater
                permissions.disputeFiledAt?.let { message.created > it } == true -> LineItemState.Disputed
                lineItem.issue?.raised?.let { message.created > it.timestamp } == true -> LineItemState.Issue
                else -> LineItemState.NotApproved
            }
            val state = message.responseType.resultantState(base)
            card - (LineItemStateSemantic(state)+BoldSemantic).onNext - stack {
                row {
                    spacing = AppDimensions.expandButtonSpace
                    centered - unreadIconWhen { unread() }
                    body {
                        content =
                            if (state == LineItemState.RequestingReview)
                                "Sign-Off ${
                                    messages.count {
                                        it.created <= message.created && (it.responseType.resultantState(
                                            base
                                        ) == LineItemState.RequestingReview)
                                    }
                                }"
                            else state.displayName
                    }
                }
            }

            padded - col {
                row {
                    centered - bodyBold(if (message.clientMessage) client.name else contractor.name)
                    centered - smallBody(message.created.format(Formats.mmddyyyy_hmm))
                }

                body {
                    content = "(${message.responseType.displayName})    ${message.message}"
                    wordBreak = WordBreak.BreakAll
                }

                if (message.attachments.isNotEmpty()) label2("Attachments") {
                    renderFiles(Constant(message.attachments), 5.rem)
                }
            }
        }

        renderReplies(message)
    }

    private fun ViewWriter.renderReplies(message: LineItemCompletionMessage) {
        val messageReplies = replies.shareFilter { it.completionMessage == message._id }

        val viewReplies = LazyProperty {
            val userId = notNullSession().userId
            replies().filter { it.completionMessage == message._id }.any { userId !in it.sentAndReadBy }
        }

        val newReply = Property<String?>(null)

        compact - row {
            expanding - SmallBodySemantic.onNext - leftExpandButton(viewReplies, highlightOnExpand = false) {
                centered - text {
                    ::content {
                        messageReplies().size.let {
                            when (it) {
                                0 -> "No Replies"
                                1 -> "1 Reply"
                                else -> "$it Replies"
                            }
                        }
                    }
                }
            }
            textButton - button {
                ::exists { messageReplies().isEmpty() }
                smallBody("Reply")
                onClick {
                    viewReplies.value = true
                    newReply.value = ""
                }
            }
        }

        onlyWhen { viewReplies() } - indentedCol(Indent.Section.before, 0.px) {
            col commentCol@{
                spacing = theme.spacing*1.4

                forEachUpdating(messageReplies) { reply ->
                    compact - col {
                        spacing = AppDimensions.cornerRadii
                        row {
                            spacing = AppDimensions.expandButtonSpace
                            centered - unreadIconWhen { notNullSession().userId !in reply().sentAndReadBy }
                            centered - body {
                                ::content { reply().senderName }
                            }
                            centered - smallBody {
                                ::content { reply().created.format(Formats.mmddyyyy_hmm) }
                            }
                        }
                        lightSection - stack {
                            col {
                                spacing = 0.px
                                body {
                                    ::content { reply().message }
                                }
                                atEnd - textButton - button {
                                    spacing = 2.dp
                                    existsDefaultFalse { messageReplies().lastOrNull()?._id == reply()._id }
                                    smallBody("Reply")
                                    onClick {
                                        newReply.value = ""
                                    }
                                }
                            }
                        }
                    }
                }
            }

            onlyWhen { newReply() != null } - col {
                space(0.1)
                field2("New Reply") {
                    sizeConstraints(height = 8.rem) - textArea {
                        content bind newReply.lens(
                            get = { it ?: "" },
                            set = { it }
                        )
                    }
                }
                atEnd - row {
                    secondaryButton - button {
                        specCenteredText("Discard")
                        onClick { newReply.value = null }
                    }
                    primaryButton - button {
                        specCenteredText("Send Reply")
                        onClick {
                            val session = notNullSession()
                            session.completionMessageComments.insert(
                                CompletionMessageComment(
                                    completionMessage = message._id,
                                    project = project._id,
                                    lineItem = lineItem._id,
                                    clientMessage = isClient,
                                    sender = session.userId,
                                    message = newReply() ?: return@onClick
                                )
                            )
                            newReply.value = null
                        }
                    }
                }
            }

            space(0.25)
        }
    }

    private fun FormAction(
        name: String,
        displayName: (ReactiveContext.()->String)? = null,
        theme: ThemeDerivation = PrimaryButtonSemantic,
        requiresValidMessage: Boolean = true,
        additionalConstraint: (ReactiveContext.() -> Boolean)? = null,
        action: suspend ()->Unit
    ) = ButtonAction(
        name,
        theme,
        displayName,
        enabled = if (requiresValidMessage || additionalConstraint != null) {
            { (if (requiresValidMessage) allValid() else true) and (additionalConstraint?.invoke() != false) }
        } else null,
        action = action
    )

    private fun ViewWriter.confirmationDialog(header: String, body: String, buttonText: String, confirmationTheme: ThemeDerivation = PrimaryButtonSemantic, action: suspend ()->Unit) {
        secondDialog.value = GenericConfirmationDialog(
            header,
            body,
            confirmationText = buttonText,
            confirmationTheme = confirmationTheme,
            dismiss = { dismissSecondDialog() }
        ) {
            if (it) action()
        }
    }

    private fun ViewWriter.acceptAction(proposal: CompletionResponse.NeedsApproval, theme: ThemeDerivation = PrimaryButtonSemantic) =
        FormAction(
            proposal.actionName(),
            requiresValidMessage = false,
            theme = theme
        ) {
            val text = proposal.confirmationText(lineItem.wraps)
            confirmationDialog(text.first, text.second, text.third) {
                val session = notNullSession()
                response set CompletionResponse.Accept(
                    proposal,
                    session.currentAccess()
                )
                sendResponse()
                closeForm()
            }
        }

    private fun ViewWriter.actionBar() {
        val primaryAction =
            if (isClient) {
                if (previousMessage == null) null
                else permissions.mostRecentCompletingProposal?.takeIf { permissions.canAccept }?.let { acceptAction(it) }
            }
            else when {
                permissions.mustRequestComplete ->
                    FormAction("Request Sign Off", requiresValidMessage = false) {
                        secondDialog.value = WorkItemRequestSignOffDialog(listOf(lineItem), messages) {
                            dismissSecondDialog()
                            if (it) closeForm()
                        }
                    }
                permissions.mustRequestReviewOfRemedy ->
                    FormAction("Request Sign Off", requiresValidMessage = false) {
                        confirmationDialog(
                            "Is the remedy ready for sign off?",
                            "Confirmation text for remedy request sign off",
                            "Request Sign Off"
                        ) {
                            val remedy: CompletionResponse.Remedy = messages.asReversed().firstNotNullOfOrNull { it.responseType as? CompletionResponse.Remedy }
                                ?: notFoundError<CompletionResponse.Remedy>()

                            response set CompletionResponse.Remedy.RemedyComplete(remedy)
                            sendResponse()
                            closeForm()
                        }
                    }
                permissions.awaitingResponse || permissions.contractorCanProposeResolution -> {
                    val actionName: ReactiveContext.() -> String = {
                        when (response()) {
                            is CompletionResponse.RequestComplete -> "Request Complete"

                            CompletionResponse.KeepWorking -> "Keep Working"
                            is CompletionResponse.Handled -> "Submit Handled"
                            CompletionResponse.MoreInfo -> "Request More Info"
                            is CompletionResponse.ForceComplete -> "Force Complete"

                            CompletionResponse.RequestResolveLater -> "Resolve Later"

                            CompletionResponse.Appeal -> "Submit Appeal"
                            is CompletionResponse.Credit -> "Submit Credit"
                            is CompletionResponse.Remedy -> "Submit Remedy Plan"

                            is CompletionResponse.RequestSiteVisit -> "Request Visit"

                            else -> "Select a Response"
                        }
                    }

                    FormAction(
                        "Send Response",
                        displayName = actionName
                    ) {
                        val a = actionName()
                        confirmationDialog(
                            a,
                            "$a confirmation text.",
                            a
                        ) {
                            sendResponse()
                            closeForm()
                        }
                    }
                }
                else -> null
            }

        val secondaryAction: Readable<ButtonAction?> = shared {
            when {
                raiseIssue() -> {
                    FormAction(
                        "Submit Issue",
                        theme = IssueButton,
                        additionalConstraint = { if (permissions.mustRaiseIssue) raiseIssue() else true }
                    ) {
                        secondDialog.value = GenericConfirmationDialog(
                            "Submit Issue",
                            "Filler text for submit issue",
                            confirmationText = "Submit Issue",
                            confirmationTheme = WarningButtonSemantic(),
                            dismiss = { dismissSecondDialog() },
                        ) {
                            if (it) {
                                sendResponse()
                                closeForm()
                            }
                        }
                    }
                }
                fileDispute() != null ->
                    FormAction(
                        "Submit Dispute",
                        theme = DisputeButton,
                        additionalConstraint = { if (permissions.mustFileDispute) fileDispute() != null else true }
                    ) {
                        if (fileDispute.value == DisputeType.ProjectWide)
                            confirmationDialog(
                                "File Project-Wide Dispute?",
                                "This will put a hold on all other work items in the project.",
                                "File Dispute",
                                confirmationTheme = DisputeButton
                            ) {
                                sendResponse()
                                closeForm()
                            }
                        else
                            confirmationDialog(
                                "File Dispute on Work Item?",
                                "This will prevent further work on the work item until an agreement is reached by both parties. Work on other work items can continue.",
                                "File Dispute",
                                confirmationTheme = DisputeButton
                            ) {
                                sendResponse()
                                closeForm()
                            }
                    }

                permissions.canLockProject ->
                    FormAction("Lock Project", theme = DisputeButton, requiresValidMessage = false) {
                        confirmationDialog(
                            "Lock Project?",
                            "Locking the project freezes the state of all work. It can only be unlocked by joint resolution or arbitration.",
                            "Lock Project",
                            confirmationTheme = DisputeButton
                        ) {
                            val session = notNullSession()
                            val dispute = session.disputes
                                .query(
                                    Query(limit = 1) {
                                        (it.lineItem.notNull.itemId eq lineItem._id) and (it.state eq DisputeState.Ongoing)
                                    }
                                )
                                .awaitSuspending()
                                .firstOrNull() ?: notFoundError<Dispute>()

                            session.nonCached.dispute.lockProject(dispute._id)
                            closeForm()
                        }
                    }
//                permissions.canDeny ->
//                    ButtonAction(
//                        "Deny ${previousMessage?.responseType?.shortDisplayName}",
//                        theme = NotReadyButton,
//                        enabled = { message.valid() }
//                    ) {
//                        val actionName = "Deny ${previousMessage?.responseType?.displayName}"
//                        confirmationDialog(
//                            "$actionName?",
//                            "Insert denial text here",
//                            actionName,
//                        ) {
//                            response set CompletionResponse.Deny
//                            sendResponse()
//                            closeForm()
//                        }
//                    }
                isClient && previousMessage?.responseType == CompletionResponse.MoreInfo ->
                    FormAction("Provide Info", theme = SecondaryButtonSemantic) {
                        sendResponse()
                        closeForm()
                    }

                isClient && permissions.run {
                    mostRecentProposal != null &&
                            mostRecentProposal != mostRecentCompletingProposal
                } -> acceptAction(permissions.mostRecentProposal!!, SecondaryButtonSemantic)

                isClient &&
                        permissions.baseState <= LineItemState.Issue &&
                        permissions.awaitingResponse &&
                        !(permissions.mustEscalate || permissions.disputeWasFiled) ->
                    FormAction(
                        if (permissions.issueWasRaised) "Resubmit Issue" else "Not Ready",
                        theme = if (permissions.issueWasRaised) IssueButton else NotReadyButton
                    ) {
                        confirmationDialog(
                            if (permissions.issueWasRaised) "Resubmit Issue" else "Not Ready",
                            if (permissions.issueWasRaised) "Resubmit issue confirmation text" else "Not ready confirmation text",
                            if (permissions.issueWasRaised) "Resubmit Issue" else "Send Not Ready",
                            confirmationTheme = if (permissions.issueWasRaised) IssueButton else NotReadyButton
                        ) {
                            sendResponse()
                            closeForm()
                        }
                    }
                else -> null
            }
        }

        val tertiaryAction = shared {
            when {
                !proposeResolution() && isClient && permissions.canProposeResolution ->
                    FormAction("Propose", theme = SecondaryButtonSemantic, requiresValidMessage = false) {
                        confirmationDialog(
                            "Propose Resolution?",
                            "Do you have a resolution in mind to end the dispute? If so, you can propose a resolution to the contractor. Once sent, the contractor will review it and send back a formal proposal for you to accept or deny.",
                            "Propose Resolution"
                        ) {
                            proposeResolution.value = true
                            if (isClient) response set CompletionResponse.ClientResolution
                        }
                    }
                proposeResolution() ->
                    FormAction(
                        "Submit Proposal",
                        displayName =
                            if (isClient) null
                            else { {
                                when (response()) {
                                    CompletionResponse.Appeal -> "Submit Appeal"
                                    is CompletionResponse.Credit -> "Submit Credit"
                                    is CompletionResponse.Remedy -> "Submit Remedy"
                                    else -> "Select Resolution"
                                }
                            } },
                        theme = SecondaryButtonSemantic
                    ) {
                        val actionName = "Submit ${response()?.displayName}"
                        confirmationDialog(
                            "$actionName?",
                            "Filler text for submit resolution proposal",
                            actionName
                        ) {
                            sendResponse()
                            closeForm()
                        }
                    }
                isClient && permissions.awaitingResponse && messages.size == 1 ->
                    FormAction("Adjustment", theme = SecondaryButtonSemantic, requiresValidMessage = false) {
                        secondDialog.value =
                            if (scopeInfo != null) MinorAdjustmentsDialog(
                                lineItems = notNullSession.awaitSuspending().lineItems.query(Query { it.scopeView eq scopeInfo.id }).awaitSuspending(),
                                name = scopeInfo.name,
                                dismiss = { accepted ->
                                    secondDialog.value = null
                                    if (accepted) closeForm()
                                }
                            )
                            else MinorAdjustmentsDialog(lineItem.wraps) { accepted ->
                                secondDialog.value = null
                                if (accepted) closeForm()
                            }
                    }

                else -> null
            }
        }

        rowCollapsingToColumn(AppDimensions.wideCollapseWidth) {
            if (permissions.canRespond) tertiaryButton - attachementButton()
            expanding - space()
            button {
                applyReactiveActionFormatting(tertiaryAction)
            }
            button {
                applyReactiveActionFormatting(secondaryAction)
            }
            if (primaryAction != null) button {
                applyActionFormatting(primaryAction)
            }
        }
    }

    private fun ViewWriter.attachementButton() = button {
        specCenteredText("+ Attachment")
        onClick("Upload Attachments") {
            ExternalServices.requestFiles()
                .map { file ->
                    val preview = FilePreview.LocalFile(file)
                    files.add(
                        FileData(
                            name = Property(""),
                            preview = preview,
                            original = preview
                        )
                    )
                    async {
                        preview.uploadAttachment(notNullSession().nonCached.lineItemCompletionMessage::uploadFileForRequest)
                    }
                }
                .awaitAll()
        }
    }


    companion object {
        // This is only used as a dialog, so we can have all the data at launch with a suspending "constructor"

        suspend fun createDialog(
            lineItem: AdjustedLineItem,
            project: Project,
            lens: ProjectLens,
            scope: ScopeSetInfo? = null
        ): LineItemCompletionForm {
            val session = notNullSession()

            val messages = session.lineItemCompletionMessages.query(
                Query(
                    limit = Int.MAX_VALUE,
                    condition = condition { it.lineItem eq lineItem._id },
                    orderBy = sort { it.created.ascending() }
                )
            )()

            val replies = session.completionMessageComments.query(
                Query(
                    limit = Int.MAX_VALUE,
                    condition = condition { it.lineItem eq lineItem._id },
                    orderBy = sort { it.created.ascending() }
                )
            )

            val contractor = session.contractors[project.contractor]() ?: notFoundError<Contractor>()
            val client = session.clients[project.client ?: throw IllegalStateException("Project has no client")]() ?: notFoundError<Client>()

            return LineItemCompletionForm(
                lineItem,
                project,
                messages,
                replies,
                client,
                contractor,
                lens,
                scope
            )
        }
    }
}